// Formik and Enzyme are incompatible, which explains some of the very strange sidestepping of istanbul and ts-lint present in this file.

import { CircularProgress } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import { StyledComponentProps, StyleRules, withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import ChevronRight from '@material-ui/icons/ChevronRight';
import { Formik, FormikProps } from 'formik';
import i18next from 'i18next';
import * as React from 'react';
import { Component } from 'react';
import { Trans } from 'react-i18next';
import { areCookiesEnabled } from '../../services/CookieService';
import RegexService from '../../services/RegexService';
import ScarecrowPropertiesService from '../../services/ScarecrowPropertiesService';

export interface RegistrationSubmissionValues {
  type: number;
  password: string;
  confirmationCode?: string;
}

interface State {
  passwordMinLength: number;
}

interface Props {
  pending: boolean;
  country: string;
  confirmationCode?: string;
  areCookiesAccepted: boolean;
  emailAddress: string;
  toggleCookieModal: () => void;
  onSubmit: (values: RegistrationSubmissionValues) => void;
}

const styles: StyleRules = {
  form: {
    padding: '0px',
    display: 'flex',
    alignItems: 'left',
    justifyContent: 'space-around',
    flexDirection: 'column',
  },
  formControl: {
    marginTop: '18px',
    marginBottom: '4px',
    minWidth: '200px',
  },
  subtitle1: {
    color: '#2b7bb9',
    fontSize: '20px',
    fontWeight: 400,
  },
  text: {
    paddingTop: '16px',
    fontSize: '16px',
    paddingLeft: '40px',
  },
  body1: {
    paddingTop: '10px',
    fontSize: '12px',
    fontWeight: 'normal',
    color: 'rgba(0, 0, 0, 0.87)',
  },
  wordWrap: {
    wordWrap: 'break-word',
  },
};

class CustomerFlowRegistrationForm extends Component<Props & StyledComponentProps, State> {
  private initialValues;

  constructor(props) {
    super(props);

    this.validate = this.validate.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.state = {
      passwordMinLength: 14,
    };

    this.initialValues = {
      createPass: '',
      confirmPass: '',
    };

    this.initialValues.confCode = this.props.confirmationCode || '';
  }

  public componentDidMount() {
    ScarecrowPropertiesService.subscribe({
      next: (function (props) {
        this.setState({
          passwordMinLength: props.passwordMinLength,
        });
      }).bind(this),
    });
  }

  public render() {
    const classes = this.props.classes!;
    const minLength = Number(this.state.passwordMinLength);

    return (
      <Formik initialValues={this.initialValues} validate={this.validate} onSubmit={this.onSubmit}>
        {(formik) => (
          <form onSubmit={formik.handleSubmit} className={classes.form}>
            {this.generateRegionalFields(classes, formik)}
            <Tooltip title={i18next.t('password_tooltip', { minLength })} enterTouchDelay={1} interactive>
              <Grid item xs={12}>
                <FormControl className={classes.formControl}>
                  <TextField
                    id="createPass"
                    label={<Trans i18nKey="password" />}
                    type="password"
                    value={formik.values.createPass}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={/* istanbul ignore next */ formik.touched.createPass && !!formik.errors.createPass}
                    helperText={/* istanbul ignore next */ formik.touched.createPass && formik.errors.createPass}
                  />
                </FormControl>
              </Grid>
            </Tooltip>
            <Grid item xs={12}>
              <FormControl className={classes.formControl}>
                <TextField
                  id="confirmPass"
                  label={<Trans i18nKey="passwordTwo" />}
                  type="password"
                  value={formik.values.confirmPass}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={/* istanbul ignore next */ formik.touched.confirmPass && !!formik.errors.confirmPass}
                  helperText={/* istanbul ignore next */ formik.touched.confirmPass && formik.errors.confirmPass}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl className={classes.formControl}>
                <Button variant="contained" color="primary" type="submit" disabled={this.shouldDisableSubmit(formik)}>
                  <Trans i18nKey="submit" />
                  {this.props.pending ? <CircularProgress variant="indeterminate" size={15} /> : <ChevronRight />}
                </Button>
              </FormControl>
            </Grid>
          </form>
        )}
      </Formik>
    );
  }

  private generateRegionalFields(classes: Partial<Record<string, string>>, formik: FormikProps<any>): React.JSX.Element {
    if (this.props.confirmationCode) {
      return (
        <Grid item md={4} xs={12}>
          <TextField
            type="hidden"
            id="confCode"
            value={this.props.confirmationCode}
          />
        </Grid>
      );
    }
    return (
      <Grid item md={4} xs={12}>
        <FormControl className={classes.formControl}>
          <TextField
            id="confCode"
            label={<Trans i18nKey="confirmation_code" />}
            value={this.props.confirmationCode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={/* istanbul ignore next */ formik.touched.confCode && !!formik.errors.confCode}
            helperText={/* istanbul ignore next */ formik.touched.confCode && formik.errors.confCode}
          />
        </FormControl>
      </Grid>
    );
  }

  private validate(values) {
    let errors = {};
    if (values.confCode === '') {
      errors = { ...errors, confCode: i18next.t('required_field') };
    }

    errors = { ...errors, ...this.validatePassword(values.createPass) };

    if (values.confirmPass !== values.createPass) {
      errors = { ...errors, confirmPass: i18next.t('password_fields_must_match') };
    }

    return errors;
  }

  private validatePassword(password: string) {
    let error = {};

    const max = 128;
    const minLength = Number(this.state.passwordMinLength);

    if (password === '') {
      error = { createPass: i18next.t('required_field') };
    } else if (password.length > max) {
      error = { createPass: i18next.t('max_length', { max }) };
    } else if (password.toLowerCase().indexOf('password') !== -1) {
      error = { createPass: i18next.t('password_cannot_contain_password') };
    } else if (!RegexService.validPasswordCharsRegex.test(password)) {
      error = { createPass: i18next.t('password_invalid_characters') };
    } else if (!this.satisfiesPasswordCharCategories(password)) {
      error = { createPass: i18next.t('password_complexity_error') };
    } else if (this.containsEmailSubstring(password)) {
      error = { createPass: i18next.t('password_cannot_contain_username') };
    } else if (password.length < minLength) {
      error = { createPass: i18next.t('min_length', { minLength }) };
    }

    return error;
  }

  private containsEmailSubstring(password: string): boolean {
    // eslint-disable-next-line prefer-regex-literals
    const PASSWORD_TOKEN_DELIMITERS = new RegExp(/[._\-@]/);
    const PASSWORD_TOKEN_MIN_LENGTH = 3;
    const { emailAddress = '' } = this.props;

    const substrings = (emailAddress.toLowerCase())
      .split(PASSWORD_TOKEN_DELIMITERS)
      .filter((substring) => substring.length >= PASSWORD_TOKEN_MIN_LENGTH);
    return substrings.some((substring) => (password.toLowerCase()).includes(substring));
  }

  private satisfiesPasswordCharCategories(password: string): boolean {
    let count = 0;
    if (RegexService.digitsRegex.test(password)) {
      count++;
    }
    if (RegexService.lowercaseRegex.test(password)) {
      count++;
    }
    if (RegexService.uppercaseRegex.test(password)) {
      count++;
    }
    if (RegexService.specialCharsRegex.test(password)) {
      count++;
    }
    return count >= 3;
  }

  private shouldDisableSubmit(formik) {
    return !formik.isValid || !formik.dirty || this.props.pending;
  }

  private onSubmit(values) {
    if (!areCookiesEnabled() || !this.props.areCookiesAccepted) {
      this.props.toggleCookieModal();
    } else {
      const registrationResult: RegistrationSubmissionValues = {
        password: values.confirmPass,
        type: 1,
      };

      registrationResult.confirmationCode = values.confCode;

      this.props.onSubmit(registrationResult);
    }
  }
}

export default withStyles(styles)(CustomerFlowRegistrationForm);
